import { useEffect, useState } from 'react';
import type { AgencyTaskView, AgencyTaskAction } from '@tsr/shared-types';
import { availableAgencyTaskActions } from '@tsr/shared-types';
import {
  acceptAgencyTask,
  acknowledgeAgencyTask,
  cancelAgencyTask,
  completeAgencyTask,
  declineAgencyTask,
  startAgencyTask,
} from './api';
import { AgencyTaskThread } from './AgencyTaskThread';
import { agencyTaskStatusBadge } from './agencyTaskUi';

/**
 * Agency-task detail (#379) — VIEWER-ROLE-AWARE so the same record renders the right
 * controls without the agency id on the client:
 *   • viewerRole='requester' (Coordination → Tasks / outbound) — the PEOC's view: status,
 *     narrative, links, the Cancel control (issued|acknowledged|accepted|in_progress →
 *     cancelled), and the target's response (Phase 3b-2a).
 *   • viewerRole='target' (Coordination → Inbox) — the tasked agency's view: the available
 *     target transitions (acknowledge / accept / decline / start / complete) as buttons,
 *     with an optional decline-reason / completion-notes input (Phase 3b-2b).
 * Both modes surface declined_reason / completion_notes when present so the requester sees
 * WHY a task was declined / the completion note. The two-party message thread is 3b-2c.
 */

function LinkRow({ label, id }: { label: string; id: string | null }): JSX.Element | null {
  if (!id) return null;
  return (
    <p className="text-slate-300">
      {label}: <span className="font-mono text-[10px] text-slate-400">{id.slice(0, 8)}…</span>
    </p>
  );
}

/** Per-action button presentation (label + colour) for the target's response controls. */
const TARGET_ACTION_UI: Record<AgencyTaskAction, { label: string; cls: string }> = {
  acknowledge: { label: 'Acknowledge', cls: 'bg-sky-700/80 hover:bg-sky-700 border-sky-600' },
  accept: { label: 'Accept', cls: 'bg-cyan-700/80 hover:bg-cyan-700 border-cyan-600' },
  decline: { label: 'Decline', cls: 'bg-rose-700/80 hover:bg-rose-700 border-rose-600' },
  start: { label: 'Start work', cls: 'bg-indigo-700/80 hover:bg-indigo-700 border-indigo-600' },
  complete: { label: 'Complete', cls: 'bg-emerald-700/80 hover:bg-emerald-700 border-emerald-600' },
  // `cancel` is the requester's action; never offered in the target control set below.
  cancel: { label: 'Cancel', cls: 'bg-rose-700/80 hover:bg-rose-700 border-rose-600' },
};

export function AgencyTaskDetailBody({
  task,
  viewerRole,
}: {
  task: AgencyTaskView;
  viewerRole: 'requester' | 'target';
}): JSX.Element {
  // Local copy so an action reflects immediately; re-seed when the selected task changes.
  // (The Coordination worklists self-heal on their own 30 s poll.)
  const [current, setCurrent] = useState<AgencyTaskView>(task);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  useEffect(() => {
    setCurrent(task);
    setError(null);
    setDeclineReason('');
    setCompletionNotes('');
  }, [task]);

  const badge = agencyTaskStatusBadge(current.task_status);
  const requesterActions = availableAgencyTaskActions(current.task_status, 'requester');
  const targetActions = availableAgencyTaskActions(current.task_status, 'target');
  const canCancel = requesterActions.includes('cancel');

  const run = (fn: () => Promise<AgencyTaskView>): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void fn()
      .then((updated) => setCurrent(updated))
      .catch(() => setError('Could not update the task. Please retry.'))
      .finally(() => setBusy(false));
  };

  const runTargetAction = (action: AgencyTaskAction): void => {
    switch (action) {
      case 'acknowledge':
        return run(() => acknowledgeAgencyTask(current.id));
      case 'accept':
        return run(() => acceptAgencyTask(current.id));
      case 'decline':
        return run(() => declineAgencyTask(current.id, declineReason.trim() || undefined));
      case 'start':
        return run(() => startAgencyTask(current.id));
      case 'complete':
        return run(() => completeAgencyTask(current.id, completionNotes.trim() || undefined));
      case 'cancel':
        return; // not a target action
    }
  };

  return (
    <div className="px-4 py-4 space-y-4" data-testid="agency-task-detail-body">
      {/* Header */}
      <div className="border-l-2 border-amber-500/60 pl-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          {viewerRole === 'target' ? 'Inbound agency task' : 'Outbound agency task'}
        </p>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold truncate">
            {viewerRole === 'target'
              ? (current.requesting_agency_name ?? 'Agency')
              : (current.target_agency_name ?? 'Agency')}
          </h2>
          <span
            className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}
            data-testid="agency-task-detail-status"
          >
            {badge.label}
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">
          {current.task_number} · {current.urgency}
          {current.response_level != null ? ` · RL ${current.response_level}` : ''}
        </p>
      </div>

      {/* Narrative */}
      <section className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
        {current.narrative}
      </section>

      {/* Links back */}
      <section className="border-t border-slate-800 pt-3 space-y-1 text-[11px] text-slate-400">
        <LinkRow label="Assistance request" id={current.assistance_request_id} />
        <LinkRow label="Incident" id={current.incident_id} />
        <LinkRow label="Resource request" id={current.resource_request_id} />
        <LinkRow label="From dispatch rec" id={current.dispatch_recommendation_id} />
        {!current.assistance_request_id &&
          !current.incident_id &&
          !current.resource_request_id &&
          !current.dispatch_recommendation_id && (
            <p className="text-slate-500">No linked records.</p>
          )}
      </section>

      {/* Response back-channel — surfaced in BOTH modes when present. */}
      {(current.declined_reason || current.completion_notes) && (
        <section className="border-t border-slate-800 pt-3 space-y-1.5 text-[11px]">
          {current.declined_reason && (
            <p className="text-rose-300" data-testid="agency-task-declined-reason">
              <span className="text-slate-400">Decline reason:</span> {current.declined_reason}
            </p>
          )}
          {current.completion_notes && (
            <p className="text-emerald-300" data-testid="agency-task-completion-notes-display">
              <span className="text-slate-400">Completion notes:</span> {current.completion_notes}
            </p>
          )}
        </section>
      )}

      {/* Action surface — role-aware. */}
      {viewerRole === 'target' ? (
        <section className="border-t border-slate-800 pt-3 space-y-2" data-testid="agency-task-target-actions">
          {targetActions.length === 0 ? (
            <p className="text-[11px] text-slate-500">
              This task is {badge.label.toLowerCase()} — no further response needed.
            </p>
          ) : (
            <>
              {targetActions.includes('decline') && (
                <textarea
                  data-testid="agency-task-decline-reason"
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Decline reason (optional — the requester will see this)"
                  rows={2}
                  className="w-full text-xs rounded bg-slate-800/60 border border-slate-700 px-2 py-1.5 text-slate-200 placeholder:text-slate-500"
                />
              )}
              {targetActions.includes('complete') && (
                <textarea
                  data-testid="agency-task-completion-notes"
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Completion notes (optional — the requester will see this)"
                  rows={2}
                  className="w-full text-xs rounded bg-slate-800/60 border border-slate-700 px-2 py-1.5 text-slate-200 placeholder:text-slate-500"
                />
              )}
              <div className="flex flex-wrap gap-2">
                {targetActions.map((action) => (
                  <button
                    key={action}
                    type="button"
                    data-testid={`agency-task-action-${action}`}
                    disabled={busy}
                    onClick={() => runTargetAction(action)}
                    className={`flex-1 min-w-[6rem] text-sm py-1.5 rounded border text-white font-medium disabled:opacity-50 disabled:cursor-wait ${TARGET_ACTION_UI[action].cls}`}
                  >
                    {TARGET_ACTION_UI[action].label}
                  </button>
                ))}
              </div>
            </>
          )}
          {error && <p className="text-[11px] text-rose-300">{error}</p>}
        </section>
      ) : (
        <section className="border-t border-slate-800 pt-3 space-y-1.5">
          {canCancel ? (
            <button
              type="button"
              data-testid="agency-task-cancel-btn"
              disabled={busy}
              onClick={() => run(() => cancelAgencyTask(current.id))}
              className="w-full text-sm py-1.5 rounded bg-rose-700/80 hover:bg-rose-700 border border-rose-600 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
            >
              {busy ? 'Cancelling…' : 'Cancel task'}
            </button>
          ) : (
            <p className="text-[11px] text-slate-500">
              This task is {badge.label.toLowerCase()} — no further requester action.
            </p>
          )}
          {error && <p className="text-[11px] text-rose-300">{error}</p>}
          <p className="text-[10px] text-slate-500 leading-relaxed">
            The tasked agency’s acknowledge / accept / decline / progress response is tracked
            here as it arrives.
          </p>
        </section>
      )}

      {/* Two-party message thread (3b-2c) — visible to BOTH parties so a task carries its
          conversation in the COP. Reuses the shipped MissionThreadPanel via AgencyTaskThread. */}
      <AgencyTaskThread task={current} viewerRole={viewerRole} />
    </div>
  );
}
